import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiFetch } from "../framework/api"
import { useAuthToken } from "./useAuthToken"

type PipelineVersionRow = {
  uid: string
  createdAt: string
  active: boolean
  ready: boolean
}

type PipelineVersionsResponse = {
  versions: PipelineVersionRow[]
}

// Query hook - fetch pipeline versions
export function usePipelineVersions() {
  const token = useAuthToken()
  
  return useQuery({
    queryKey: ["pipelineVersions"],
    queryFn: async () => {
      const data = await apiFetch<PipelineVersionsResponse>("/admin/pipeline-versions", {
        token,
        method: "GET",
      })
      return data.versions
    },
  })
}

// Mutation hook - activate a pipeline version
export function useActivatePipelineVersion() {
  const token = useAuthToken()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (uid: string) => {
      await apiFetch("/admin/pipeline-versions/activate", {
        token,
        method: "POST",
        body: JSON.stringify({ uid }),
      })
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["pipelineVersions"] })
    },
  })
}

// Mutation hook - import pipeline version
export function useImportPipelineVersion() {
  const token = useAuthToken()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (uid: string) => {
      const data = await apiFetch("/admin/pipeline-versions/import", {
        token,
        method: "POST",
        body: JSON.stringify({ uid }),
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipelineVersions"] })
    },
  })
}

// Mutation hook - delete pipeline version data
export function useDeletePipelineVersionData() {
  const token = useAuthToken()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (uid: string) => {
      const data = await apiFetch("/admin/pipeline-versions/delete-data", {
        token,
        method: "POST",
        body: JSON.stringify({ uid }),
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pipelineVersions"] })
    },
  })
}
